import { useState, useCallback, useMemo, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Building2 } from "lucide-react";
import styles from "./RegionManager.module.css";
import SelectBox from "../../../components/BaseComponents/SelectBox/SelectBox";
import AddRegionModal from "../../../components/GeoData/Region/AddRegionModal";
import ToggleSwitch from "../../../components/BaseComponents/Toggle/ToggleSwitch";
import DeleteConfirmModal from "../../../components/BaseComponents/DeleteConfirmModal/DeleteConfirmModal";
import { useToast } from "../../../context/ToastContext";
import {
  useGetRegions,
  useCreateRegion,
  useUpdateRegion,
  useDeleteRegion,
} from "../../../api/geo/useRegions";
import { useGetCountries } from "../../../api/geo/useCountries";
import { assignHolidayTypesToRegion } from "../../../api/geo/useHolidayTypes";

/** Map API region to row shape (and to modal initialData with marketing) */
const toRow = (r) => ({
  ...r,
  marketing: {
    title: r.title ?? "",
    para1: r.paragraph1 ?? "",
    para2: r.paragraph2 ?? "",
  },
});

// Pending toggle changes: { [rowId]: { active? } }
const getDisplayValue = (row, field, pendingChanges) =>
  pendingChanges[row.id]?.[field] ?? row[field];

const RegionManager = () => {
  const { showToast } = useToast();
  const [searchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [countryFilter, setCountryFilter] = useState("");

  // Sync country filter from URL query param (e.g. when navigating from Countries page)
  useEffect(() => {
    const countryId = searchParams.get("countryId") || "";
    setCountryFilter(countryId);
    if (countryId) setPage(1);
  }, [searchParams]);
  const [showModal, setShowModal] = useState(false);
  const [mode, setMode] = useState("add");
  const [selectedRow, setSelectedRow] = useState(null);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [pendingChanges, setPendingChanges] = useState({});
  const [toggleSaveLoading, setToggleSaveLoading] = useState(false);

  const { data: regionsData, pagination, loading, error, refetch } = useGetRegions({
    page,
    limit,
    search: search || undefined,
    countryId: countryFilter || undefined,
    sortBy: "name",
    sortOrder: "ASC",
  });

  const { data: countriesData } = useGetCountries({
    page: 1,
    limit: 200,
    sortBy: "name",
    sortOrder: "ASC",
  });

  const { execute: createRegion, loading: createLoading } = useCreateRegion();
  const { execute: updateRegion, loading: updateLoading } = useUpdateRegion();
  const { execute: deleteRegion, loading: deleteLoading } = useDeleteRegion();

  const rows = (regionsData || []).map(toRow);
  const countries = (countriesData || []).map((c) => ({ id: c.id, name: c.name }));
  const saveLoading = createLoading || updateLoading;
  const hasPendingChanges = Object.keys(pendingChanges).length > 0;

  const pageNumbers = useMemo(() => {
    const total = pagination?.totalPages ?? 1;
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const current = page;
    const pages = new Set([1, total]);
    for (let i = Math.max(1, current - 2); i <= Math.min(total, current + 2); i++) pages.add(i);
    const sorted = [...pages].sort((a, b) => a - b);
    const result = [];
    let prev = 0;
    for (const p of sorted) {
      if (p > prev + 1) result.push("…");
      result.push(p);
      prev = p;
    }
    return result;
  }, [pagination?.totalPages, page]);

  // Auto-search: debounce the input so the list filters as you type (no button).
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
      setPendingChanges({});
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const applyToggle = useCallback((row, field) => {
    setPendingChanges((prev) => {
      const current = prev[row.id] ?? {};
      const serverVal = row[field];
      const pendingVal = current[field];
      const nextVal = pendingVal !== undefined ? !pendingVal : !serverVal;
      return {
        ...prev,
        [row.id]: { ...current, [field]: nextVal },
      };
    });
  }, []);

  const handleAdd = () => {
    setMode("add");
    setSelectedRow(null);
    setShowModal(true);
  };

  const handleEdit = (row) => {
    setMode("edit");
    setSelectedRow(row);
    setShowModal(true);
  };

  const handleDeleteClick = (row) => setRowToDelete(row);

  const handleConfirmDelete = async () => {
    if (!rowToDelete) return;
    try {
      await deleteRegion(rowToDelete.id);
      setPendingChanges((prev) => {
        const next = { ...prev };
        delete next[rowToDelete.id];
        return next;
      });
      refetch();
      setRowToDelete(null);
      showToast("Region deleted successfully", "success");
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to delete region", "error");
    }
  };

  const handleSaveToggles = async () => {
    const ids = Object.keys(pendingChanges);
    if (ids.length === 0) return;
    setToggleSaveLoading(true);
    try {
      for (const id of ids) {
        const patch = pendingChanges[id];
        const row = rows.find((r) => String(r.id) === String(id));
        const payload = { active: patch.active ?? row?.active };
        await updateRegion(id, payload);
      }
      setPendingChanges({});
      refetch();
      showToast("Changes saved successfully", "success");
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to save changes", "error");
    } finally {
      setToggleSaveLoading(false);
    }
  };

  const handleSave = async (payload) => {
    try {
      const { holidayTypeIds = [], ...regionPayload } = payload;

      if (mode === "add") {
        const res = await createRegion(regionPayload);
        const newRegionId = res?.data?.id ?? res?.id;
        if (newRegionId != null && holidayTypeIds.length > 0) {
          await assignHolidayTypesToRegion(newRegionId, holidayTypeIds);
        }
        showToast("Region created successfully", "success");
      } else if (selectedRow?.id) {
        await updateRegion(selectedRow.id, regionPayload);
        await assignHolidayTypesToRegion(selectedRow.id, holidayTypeIds);
        showToast("Region updated successfully", "success");
      }
      setShowModal(false);
      refetch();
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to save region";
      showToast(msg, "error");
      throw new Error(msg);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.toolbar}>
        <div className={styles.manager}>Regions</div>
        <button className={styles.add} onClick={handleAdd}>
          + Add Region
        </button>
      </div>

      <div className={styles.tagLine}>
        <span className={styles.tagIcon} aria-hidden>ℹ</span>
        <span>Regions group cities for broader geographic organization. Useful for reporting and filtering by area.</span>
      </div>

      <div className={styles.filterBar}>
        <div className={styles.searchForm}>
          <input
            type="text"
            placeholder="Search region or code..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className={styles.searchInput}
          />
        </div>
        <SelectBox
          placeholder="Country: All"
          value={countryFilter}
          options={[
            { label: "All Countries", value: "" },
            ...countries.map((c) => ({
              label: c.name,
              value: String(c.id),
            })),
          ]}
          onChange={(e) => {
            setCountryFilter(e.target.value);
            setPage(1);
            setPendingChanges({});
          }}
        />
      </div>

      {error && (
        <div className={styles.error}>
          {error}
        </div>
      )}

      {loading ? (
        <div className={styles.loading}>Loading regions…</div>
      ) : (
        <>
          <div className={styles.resultsSummary}>
            Showing {rows.length} of {pagination?.total ?? 0} regions
          </div>
          <div className={styles.tableSection}>
            <div className={styles.tableCard}>
              <table className={styles.table}>
              <thead>
                <tr>
                  <th>Region Name</th>
                  <th>Code</th>
                  <th>Country</th>
                  <th>Status</th>
                  <th>View Cities</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={6} className={styles.noResults}>
                      No regions found.
                    </td>
                  </tr>
                ) : (
                rows.map((row) => (
                  <tr key={row.id}>
                    <td>{row.name}</td>
                    <td>{row.code}</td>
                    <td>{row.countryName ?? countries.find((c) => c.id === row.countryId)?.name}</td>
                    <td>
                      <ToggleSwitch
                        checked={!!getDisplayValue(row, "active", pendingChanges)}
                        onChange={() => applyToggle(row, "active")}
                      />
                    </td>
                    <td>
                      <Link
                        to={`/geodata/cities?countryId=${row.countryId}&regionId=${row.id}`}
                        className={styles.viewLink}
                      >
                        <Building2 size={14} aria-hidden />
                        <span>View cities</span>
                      </Link>
                    </td>
                    <td className={styles.rowActions}>
                      <button onClick={() => handleEdit(row)}>Edit</button>
                      <button onClick={() => handleDeleteClick(row)}>Delete</button>
                    </td>
                  </tr>
                )))}
              </tbody>
              </table>
            </div>

            <div className={styles.footerBar}>
              <div className={styles.pagination}>
                <span className={styles.paginationInfo}>
                  Page {pagination?.page ?? page} of {pagination?.totalPages || 1} ({pagination?.total ?? 0} total)
                </span>
                {(pagination?.totalPages ?? 1) > 1 && (
                  <div className={styles.pageNumbers}>
                    {pageNumbers.map((p, idx) =>
                      p === "…" ? (
                        <span key={`ellipsis-${idx}`} className={styles.ellipsis}>…</span>
                      ) : (
                        <button
                          key={p}
                          className={`${styles.pageBtn} ${Number(p) === Number(page) ? styles.pageBtnActive : ""}`}
                          onClick={() => {
                            setPage(p);
                            setPendingChanges({});
                          }}
                        >
                          {p}
                        </button>
                      )
                    )}
                  </div>
                )}
              </div>
              <button
                type="button"
                className={styles.saveChangesBtn}
                onClick={handleSaveToggles}
                disabled={!hasPendingChanges || saveLoading}
              >
                {toggleSaveLoading ? "Saving…" : "Save Changes"}
              </button>
            </div>
          </div>
        </>
      )}

      <AddRegionModal
        open={showModal}
        onClose={() => setShowModal(false)}
        mode={mode}
        initialData={selectedRow}
        countries={countries}
        onSave={handleSave}
        saveLoading={saveLoading}
      />

      <DeleteConfirmModal
        open={!!rowToDelete}
        onClose={() => setRowToDelete(null)}
        title="Delete region"
        itemName={rowToDelete?.name}
        onConfirm={handleConfirmDelete}
        loading={deleteLoading}
      />
    </div>
  );
};

export default RegionManager;
